import apiClient from "./axiosConfig";

// Admin billing API (/api/billing). Money is integer paise; calendar dates are IST "YYYY-MM-DD";
// `warnAt` / `lockAt` / `pausedUntil` in the tenant state are epoch ms.

export type BillingStatus =
  | "paid_up"
  | "due_soon"
  | "overdue"
  | "read_only"
  | "paused"
  | "no_billing";
export type Enforcement = "off" | "warn_only" | "read_only";
export type BillingMode = "ok" | "warning" | "read_only";
export type InvoiceStatus = "draft" | "issued" | "partially_paid" | "paid" | "void";
export type ScheduleStatus = "active" | "paused" | "ended";
export type PaymentStatus = "succeeded" | "reversed";
export type ManualPaymentMethod = "bank_transfer" | "upi" | "cheque" | "cash" | "other";
export type PaymentMethod = ManualPaymentMethod | "gateway";

export interface Paginated<T> {
  data: T[];
  page: number;
  pageSize: number;
  total: number;
}

export interface TenantRef {
  _id: string;
  name: string;
}

/** The client on its billing page. */
export interface TenantBillingRef extends TenantRef {
  /** pm2 app name of the deployment. */
  slug: string | null;
  /** Only "beta" clients accept billing changes until the production release. */
  stage: "beta" | "locked";
}

// ---- Overview ---------------------------------------------------------------------------------

export interface BillingKpis {
  outstanding: number;
  overdueAmount: number;
  overdueCount: number;
  dueSoonCount: number;
  readOnlyCount: number;
  collectedThisMonth: number;
}

export interface BillingOverviewRow {
  tenantId: string;
  tenantName: string;
  /** pm2 app name of the deployment. */
  slug: string | null;
  /** Public origin of the deployment. */
  uri: string | null;
  /** Only "beta" clients accept billing changes until the production release. */
  stage: "beta" | "locked";
  status: BillingStatus;
  enforcement: Enforcement;
  nextDue: { date: string; amount: number } | null;
  outstanding: number;
  planSummary: string | null;
  lastPaymentAt: string | null;
  /** IST date ("YYYY-MM-DD") of the latest payment; manual payments only record a date. */
  lastPaymentOn: string | null;
  link: { linked: boolean; lastSyncAt: string | null };
}

export interface BillingOverview {
  kpis: BillingKpis;
  rows: BillingOverviewRow[];
}

// ---- Analytics --------------------------------------------------------------------------------

/** A client as every billing list shows it: name, pm2 app, URL, stage, TeleOpd connection. */
export interface BillingClientIdentity {
  tenantId: string;
  tenantName: string;
  slug: string | null;
  uri: string | null;
  stage: "beta" | "locked";
  link: { linked: boolean; lastSyncAt: string | null };
}

/** What one client paid in the period (succeeded payments; reversals are left out). */
export interface CollectionsClientRow extends BillingClientIdentity {
  amount: number;
  count: number;
  lastPaidOn: string | null;
  /** Paise per payment method, e.g. { upi: 50000, gateway: 120000 }. */
  methods: Record<string, number>;
}

export interface CollectionsMonth {
  /** "YYYY-MM". */
  month: string;
  amount: number;
  count: number;
}

export type AgeingBucket = "notDue" | "d1_30" | "d31_60" | "d61_90" | "d90Plus";

/** What one client owes today on issued and part-paid invoices. */
export interface OutstandingClientRow extends BillingClientIdentity {
  outstanding: number;
  overdue: number;
  openInvoices: number;
  oldestDueDate: string | null;
  /** Days past the oldest overdue invoice's due date; 0 when nothing is overdue. */
  daysOverdue: number;
  buckets: Record<AgeingBucket, number>;
}

export interface BillingAnalytics {
  /** Both ends inclusive, IST dates. */
  range: { from: string; to: string };
  today: string;
  collections: {
    total: number;
    count: number;
    clients: CollectionsClientRow[];
    /** The last 12 months up to today, oldest first, empty months included. */
    byMonth: CollectionsMonth[];
  };
  outstanding: {
    total: number;
    overdue: number;
    notDue: number;
    buckets: Record<AgeingBucket, number>;
    clients: OutstandingClientRow[];
  };
}

export interface AnalyticsRange {
  from: string;
  to: string;
}

// ---- Account ----------------------------------------------------------------------------------

export interface BillingContact {
  name: string;
  email: string | null;
  /** +91XXXXXXXXXX: reminders and receipts go to it on WhatsApp. */
  phone: string | null;
}

export interface BillingPolicy {
  enforcement: Enforcement;
  warningDays: number;
  graceDays: number;
  audienceRoleIds: string[];
  audienceRoleNames: string[];
  extraWritablePrefixes: string[];
}

export interface BillingReminderSettings {
  enabled: boolean;
  offsetsDays: number[];
}

export interface BillingOverride {
  /** Last paused day (inclusive), "YYYY-MM-DD". */
  pausedUntil: string;
  reason: string | null;
  by: string | null;
  at: string | null;
}

export interface BillingAccount {
  _id: string | null;
  tenant: string;
  currency: string;
  contacts: BillingContact[];
  policy: BillingPolicy;
  reminders: BillingReminderSettings;
  override: BillingOverride | null;
  lastStatus: BillingStatus | null;
  lastStatusAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

// ---- Invoices, schedules, payments ----------------------------------------------------------

export interface InvoiceLineItem {
  description: string;
  qty: number;
  /** Paise; negative for discount lines. */
  unitPrice: number;
  taxRate: number;
  amount: number;
}

export interface PaymentLink {
  provider: string;
  linkId: string;
  url: string | null;
  referenceId: string | null;
  amount: number;
  status: string;
  expireBy: string | number | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export type NotifyChannel = "email" | "whatsapp";

export interface InvoiceReminder {
  dueDate: string | null;
  offsetDays: number | null;
  manual: boolean;
  claimedAt: string | null;
  sentAt: string | null;
  skipped: boolean;
  recipients: string[];
  error: string | null;
  attempts: number;
  channels: Array<{ channel: NotifyChannel; delivered: string[]; error: string | null }>;
}

export interface DueDateChange {
  from: string;
  to: string;
  reason?: string | null;
  by?: string | null;
  at?: string | null;
}

export interface Invoice {
  _id: string;
  tenant: string;
  number: string | null;
  fy: string | null;
  title: string | null;
  source: "schedule" | "manual" | string;
  schedule: string | null;
  periodIndex: number | null;
  items: InvoiceLineItem[];
  subtotal: number;
  total: number;
  amountPaid: number;
  /** Can be negative after an overpayment. */
  balance: number;
  currency: string;
  dueDate: string;
  originalDueDate: string | null;
  dueDateHistory: DueDateChange[];
  issuedAt: string | null;
  status: InvoiceStatus;
  paidAt: string | null;
  publicToken: string | null;
  payUrl: string | null;
  payable: boolean;
  paymentLinks: PaymentLink[];
  reminders: InvoiceReminder[];
  needsReview: boolean;
  reviewNote: string | null;
  notes: string | null;
  voidedAt: string | null;
  voidReason: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface SchedulePeriod {
  periodIndex: number;
  dueDate: string;
  issueDate: string;
  total: number;
}

export interface Schedule {
  _id: string;
  tenant: string;
  name: string;
  items: InvoiceLineItem[];
  total: number;
  intervalMonths: number;
  firstDueDate: string;
  anchorPeriodIndex: number;
  endDate: string | null;
  issueDaysBefore: number;
  status: ScheduleStatus;
  nextPeriodIndex: number;
  pauses: Array<{ from: string; to: string | null }>;
  notes: string | null;
  planSummary: string;
  nextPeriod: SchedulePeriod | null;
  upcoming: SchedulePeriod[];
  endedAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface Payment {
  _id: string;
  tenant: string;
  invoice: string;
  amount: number;
  currency: string;
  method: PaymentMethod | string;
  provider: string | null;
  providerPaymentId: string | null;
  instrument: string | null;
  linkId: string | null;
  reference: string | null;
  paidAt: string;
  paidOn: string | null;
  status: PaymentStatus;
  needsReview: boolean;
  reviewNote: string | null;
  reason: string | null;
  recordedBy: string | null;
  reversedAt: string | null;
  reversedBy: string | null;
  reverseReason: string | null;
  createdAt: string | null;
}

export interface BillingActivity {
  _id: string;
  tenant: string;
  type: string;
  refs: { invoice: string | null; payment: string | null; schedule: string | null };
  /** "system" or a user id. */
  actor: string;
  actorName: string | null;
  data: Record<string, unknown> | null;
  at: string;
}

// ---- Client billing page ----------------------------------------------------------------------

export interface BillingNotice {
  invoiceNumber: string;
  amountDue: number;
  currency: string;
  dueDate: string;
  payUrl: string | null;
}

export interface BillingState {
  status: BillingStatus;
  mode: BillingMode;
  /** The account's policy. */
  enforcement: Enforcement;
  /** What TeleOpd really applies: off while frozen, warn_only until it can enforce read-only. */
  appliedEnforcement: Enforcement;
  warnAt: number | null;
  lockAt: number | null;
  paused: boolean;
  /** Instant the pause ends (10:00 IST the day after the last paused day). */
  pausedUntil: number | null;
  focusInvoice: Invoice | null;
  outstanding: number;
  pastDueAmount: number;
  amountDue: number;
  notice: BillingNotice | null;
}

export interface TenantBilling {
  tenant: TenantBillingRef;
  account: BillingAccount;
  state: BillingState;
  schedules: Schedule[];
  invoices: Invoice[];
  payments: Payment[];
  activity: BillingActivity[];
  /** Which channels can carry reminders and receipts now (server config). */
  notifications: { email: boolean; whatsappReminders: boolean; whatsappReceipts: boolean };
}

export type InvoiceListItem = Invoice & { tenantName: string | null };
export type PaymentListItem = Payment & {
  invoiceNumber: string | null;
  tenantName: string | null;
};

export interface InvoiceDetail {
  invoice: Invoice;
  tenant: TenantRef | null;
  payments: Payment[];
  activity: BillingActivity[];
}

// ---- Inputs -----------------------------------------------------------------------------------

export interface AccountUpdateInput {
  contacts?: BillingContact[];
  policy?: Partial<BillingPolicy>;
  reminders?: Partial<BillingReminderSettings>;
  /** Required (with billing/approve) when the change relaxes enforcement. */
  reason?: string;
}

export interface LineItemInput {
  description: string;
  qty: number;
  /** Paise; negative for discounts. */
  unitPrice: number;
}

export interface ScheduleInput {
  name: string;
  items: LineItemInput[];
  intervalMonths: number;
  firstDueDate: string;
  endDate?: string | null;
  issueDaysBefore?: number;
  notes?: string | null;
}

export type ScheduleUpdateInput = Partial<ScheduleInput>;

export interface SchedulePreviewInput {
  firstDueDate: string;
  intervalMonths: number;
  items?: LineItemInput[];
  endDate?: string | null;
  count?: number;
  issueDaysBefore?: number;
}

export interface InvoiceCreateInput {
  title?: string;
  items: LineItemInput[];
  dueDate: string;
  notes?: string;
  issueNow?: boolean;
}

export interface InvoiceUpdateInput {
  title?: string | null;
  items?: LineItemInput[];
  dueDate?: string;
  notes?: string | null;
}

export interface ManualPaymentInput {
  amount: number;
  method: ManualPaymentMethod;
  /** "YYYY-MM-DD", today or earlier (IST). */
  paidAt: string;
  reference?: string;
  reason: string;
}

export interface InvoiceListQuery {
  tenantId?: string;
  status?: InvoiceStatus[];
  needsReview?: boolean;
  page?: number;
  pageSize?: number;
}

export interface PaymentListQuery {
  tenantId?: string;
  invoiceId?: string;
  status?: PaymentStatus;
  page?: number;
  pageSize?: number;
}

export interface ReminderResult {
  sent: boolean;
  recipients: string[];
  channels: NotifyChannel[];
  invoice: Invoice;
}

export interface PaymentResult {
  payment: Payment;
  invoice: Invoice;
}

// ---- Query keys -------------------------------------------------------------------------------

export const billingKeys = {
  all: ["billing"] as const,
  overview: () => ["billing", "overview"] as const,
  analytics: (range: AnalyticsRange) => ["billing", "analytics", range] as const,
  tenant: (tenantId: string) => ["billing", "tenant", tenantId] as const,
  invoices: (query: InvoiceListQuery) => ["billing", "invoices", query] as const,
  invoice: (invoiceId: string) => ["billing", "invoice", invoiceId] as const,
  payments: (query: PaymentListQuery) => ["billing", "payments", query] as const,
  schedulePreview: (input: SchedulePreviewInput) => ["billing", "schedule-preview", input] as const,
};

// ---- Calls ------------------------------------------------------------------------------------

const BASE = "/api/billing";

export const fetchBillingOverview = async (): Promise<BillingOverview> => {
  const res = await apiClient.get<BillingOverview>(`${BASE}/overview`);
  return res.data;
};

export const fetchBillingAnalytics = async (range: AnalyticsRange): Promise<BillingAnalytics> => {
  const res = await apiClient.get<BillingAnalytics>(`${BASE}/analytics`, { params: range });
  return res.data;
};

export const fetchTenantBilling = async (tenantId: string): Promise<TenantBilling> => {
  const res = await apiClient.get<TenantBilling>(`${BASE}/tenants/${tenantId}`);
  return res.data;
};

export const updateBillingAccount = async (
  tenantId: string,
  input: AccountUpdateInput
): Promise<BillingAccount> => {
  const res = await apiClient.put<BillingAccount>(`${BASE}/tenants/${tenantId}/account`, input);
  return res.data;
};

export const pauseEnforcement = async (
  tenantId: string,
  input: { pausedUntil: string; reason: string }
): Promise<BillingAccount> => {
  const res = await apiClient.post<BillingAccount>(`${BASE}/tenants/${tenantId}/override`, input);
  return res.data;
};

export const resumeEnforcement = async (
  tenantId: string,
  reason: string
): Promise<BillingAccount> => {
  const res = await apiClient.delete<BillingAccount>(`${BASE}/tenants/${tenantId}/override`, {
    data: { reason },
  });
  return res.data;
};

export const fetchSchedules = async (tenantId: string): Promise<Schedule[]> => {
  const res = await apiClient.get<{ data: Schedule[] }>(`${BASE}/tenants/${tenantId}/schedules`);
  return res.data.data;
};

export const previewSchedule = async (input: SchedulePreviewInput): Promise<SchedulePeriod[]> => {
  const res = await apiClient.post<SchedulePeriod[]>(`${BASE}/schedules/preview`, input);
  return res.data;
};

export const createSchedule = async (tenantId: string, input: ScheduleInput): Promise<Schedule> => {
  const res = await apiClient.post<Schedule>(`${BASE}/tenants/${tenantId}/schedules`, input);
  return res.data;
};

export const updateSchedule = async (
  scheduleId: string,
  input: ScheduleUpdateInput
): Promise<Schedule> => {
  const res = await apiClient.patch<Schedule>(`${BASE}/schedules/${scheduleId}`, input);
  return res.data;
};

export const changeScheduleStatus = async (
  scheduleId: string,
  action: "pause" | "resume" | "end"
): Promise<Schedule> => {
  const res = await apiClient.post<Schedule>(`${BASE}/schedules/${scheduleId}/${action}`);
  return res.data;
};

export const fetchInvoices = async (
  query: InvoiceListQuery = {}
): Promise<Paginated<InvoiceListItem>> => {
  const params: Record<string, string | number | boolean> = {};
  if (query.tenantId) {
    params.tenantId = query.tenantId;
  }
  if (query.status && query.status.length > 0) {
    params.status = query.status.join(",");
  }
  if (query.needsReview) {
    params.needsReview = true;
  }
  if (query.page) {
    params.page = query.page;
  }
  if (query.pageSize) {
    params.pageSize = query.pageSize;
  }
  const res = await apiClient.get<Paginated<InvoiceListItem>>(`${BASE}/invoices`, { params });
  return res.data;
};

export const fetchInvoice = async (invoiceId: string): Promise<InvoiceDetail> => {
  const res = await apiClient.get<InvoiceDetail>(`${BASE}/invoices/${invoiceId}`);
  return res.data;
};

export const createInvoice = async (
  tenantId: string,
  input: InvoiceCreateInput
): Promise<Invoice> => {
  const res = await apiClient.post<Invoice>(`${BASE}/tenants/${tenantId}/invoices`, input);
  return res.data;
};

export const updateInvoice = async (
  invoiceId: string,
  input: InvoiceUpdateInput
): Promise<Invoice> => {
  const res = await apiClient.patch<Invoice>(`${BASE}/invoices/${invoiceId}`, input);
  return res.data;
};

export const issueInvoice = async (invoiceId: string): Promise<Invoice> => {
  const res = await apiClient.post<Invoice>(`${BASE}/invoices/${invoiceId}/issue`);
  return res.data;
};

export const sendInvoiceReminder = async (invoiceId: string): Promise<ReminderResult> => {
  const res = await apiClient.post<ReminderResult>(`${BASE}/invoices/${invoiceId}/remind`);
  return res.data;
};

export const voidInvoice = async (invoiceId: string, reason: string): Promise<Invoice> => {
  const res = await apiClient.post<Invoice>(`${BASE}/invoices/${invoiceId}/void`, { reason });
  return res.data;
};

export const extendInvoiceDueDate = async (
  invoiceId: string,
  input: { dueDate: string; reason: string }
): Promise<Invoice> => {
  const res = await apiClient.post<Invoice>(`${BASE}/invoices/${invoiceId}/extend`, input);
  return res.data;
};

export const resolveInvoiceReview = async (invoiceId: string, reason: string): Promise<Invoice> => {
  const res = await apiClient.post<Invoice>(`${BASE}/invoices/${invoiceId}/resolve-review`, {
    reason,
  });
  return res.data;
};

export const recordManualPayment = async (
  invoiceId: string,
  input: ManualPaymentInput
): Promise<PaymentResult> => {
  const res = await apiClient.post<PaymentResult>(`${BASE}/invoices/${invoiceId}/payments`, input);
  return res.data;
};

export const reversePayment = async (paymentId: string, reason: string): Promise<PaymentResult> => {
  const res = await apiClient.post<PaymentResult>(`${BASE}/payments/${paymentId}/reverse`, {
    reason,
  });
  return res.data;
};

export const fetchPayments = async (
  query: PaymentListQuery = {}
): Promise<Paginated<PaymentListItem>> => {
  const params: Record<string, string | number> = {};
  if (query.tenantId) {
    params.tenantId = query.tenantId;
  }
  if (query.invoiceId) {
    params.invoiceId = query.invoiceId;
  }
  if (query.status) {
    params.status = query.status;
  }
  if (query.page) {
    params.page = query.page;
  }
  if (query.pageSize) {
    params.pageSize = query.pageSize;
  }
  const res = await apiClient.get<Paginated<PaymentListItem>>(`${BASE}/payments`, { params });
  return res.data;
};
